import type { CreateTask, Task } from '../../../lib/db/models'

/**
 * Copy every user-visible field from `source` into a fresh `CreateTask`
 * input with a new UUID. Callers pass `overrides` for anything the clone
 * should differ on (usually `order`, `sectionId`, or status resets).
 *
 * Keeping this in one place means a new Task field only needs to be added
 * here once — before, every duplicate call-site had its own hand-written
 * copy block that silently drifted when new fields were introduced.
 *
 * `tags` is cloned into a new array so later mutations on the source
 * don't leak into the duplicate.
 */
export function cloneTaskFields(
  source: Task,
  overrides?: Partial<CreateTask>,
): CreateTask & { id: string } {
  return {
    id: crypto.randomUUID(),
    title: source.title,
    description: source.description,
    status: source.status,
    attention: source.attention,
    category: source.category,
    lifeSpace: source.lifeSpace,
    projectId: source.projectId,
    sectionId: source.sectionId,
    dueDate: source.dueDate,
    dueTime: source.dueTime,
    duePeriod: source.duePeriod,
    startDate: source.startDate,
    startTime: source.startTime,
    startPeriod: source.startPeriod,
    completedAt: source.completedAt,
    estimatedDuration: source.estimatedDuration,
    actualDuration: source.actualDuration,
    durationMode: source.durationMode,
    tags: [...source.tags],
    parentTaskId: source.parentTaskId,
    pausedAt: source.pausedAt,
    ...overrides,
  }
}
